import React, { useState, useEffect } from 'react';
import { useConfig } from '../../context/ConfigContext';
import { useToast } from '../Toast';
import { C, S } from '../../utils/styles';
import StyledSelect from '../StyledSelect';

export default function EngineSettingsEditor() {
  const { config, updateEngineSettings } = useConfig();
  const addToast = useToast();
  const [draft, setDraft] = useState({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    setDraft({ ...config.engineSettings });
    setHasChanges(false);
  }, [config.engineSettings]);

  const updateField = (key, value) => {
    setDraft(prev => ({ ...prev, [key]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateEngineSettings(draft);
    setHasChanges(false);
    addToast('System defaults saved successfully');
  };

  const handleReset = () => {
    setDraft({ ...config.engineSettings });
    setHasChanges(false);
  };

  const payFrequencyOptions = [
    { value: 12, label: 'Monthly (12 periods/year)' },
    { value: 24, label: 'Semi-Monthly (24 periods/year)' },
    { value: 26, label: 'Biweekly (26 periods/year)' },
    { value: 52, label: 'Weekly (52 periods/year)' },
  ];

  const optimizationOptions = [
    { value: 'employee', label: 'Maximize Employee Benefit' },
    { value: 'employer', label: 'Maximize Employer FICA Savings' },
    { value: 'balanced', label: 'Balanced (50/50)' },
  ];

  const ficaMethodOptions = [
    { value: 'simplified', label: 'Simplified (flat rate)', desc: 'Applies 7.65% to entire premium amount' },
    { value: 'wageBaseAware', label: 'Wage-Base Aware', desc: 'Accounts for SS wage base cap - more accurate for high earners' },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header Note */}
      <div style={{ padding: '12px 16px', background: C.blueLight, borderRadius: 8, border: `1px solid ${C.blue}` }}>
        <p style={{ fontSize: 11, color: C.blue, margin: 0, lineHeight: 1.6 }}>
          <strong>Note:</strong> These are default values and system-level constants. Analysis-time controls (Pay Frequency, Optimization Strategy, Min Benefit, FUTA) are now in the Analysis Controls bar on the Individual and Workforce tabs.
        </p>
      </div>

      {/* Header with Save/Reset */}
      {hasChanges && (
        <div style={{ padding: '10px 16px', background: C.orangeLight, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: `1px solid ${C.orange}` }}>
          <span style={{ fontSize: 12, color: C.orange, fontWeight: 700 }}>You have unsaved changes</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleReset} style={{ ...S.btn, ...S.btnSecondary, fontSize: 11, padding: '6px 14px' }}>Cancel</button>
            <button onClick={handleSave} style={{ ...S.btn, ...S.btnPrimary, fontSize: 11, padding: '6px 14px' }}>Save Changes</button>
          </div>
        </div>
      )}

      {/* Default Values (used as initial values for Analysis Controls) */}
      <div style={S.card}>
        <h3 style={S.heading}>Initial Analysis Defaults</h3>
        <p style={{ fontSize: 11, color: C.textLight, margin: '0 0 12px', lineHeight: 1.5 }}>
          These values are used as the initial defaults when users open the analysis tabs. Users can change them via Analysis Controls during their session.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ ...S.label, marginBottom: 6 }}>Default Pay Frequency</label>
            <StyledSelect
              value={String(draft.payPeriodsPerYear)}
              onValueChange={(val) => updateField('payPeriodsPerYear', +val)}
              options={payFrequencyOptions.map(opt => ({ value: String(opt.value), label: opt.label }))}
            />
          </div>
          <div>
            <label style={{ ...S.label, marginBottom: 6 }}>Default Optimization Strategy</label>
            <StyledSelect
              value={draft.optimizationStrategy}
              onValueChange={(val) => updateField('optimizationStrategy', val)}
              options={optimizationOptions.map(opt => ({ value: opt.value, label: opt.label }))}
            />
          </div>
          <div>
            <label style={{ ...S.label, marginBottom: 6 }}>Default Min Employee Net Benefit</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ ...S.mono, fontSize: 12, color: C.accent, fontWeight: 700 }}>$</span>
              <input
                type="number"
                value={draft.minEmployeeNetBenefit ?? 0}
                onChange={(e) => updateField('minEmployeeNetBenefit', +e.target.value)}
                step="5"
                min="-200"
                max="200"
                style={{ ...S.input, fontSize: 12, padding: '6px 10px', flex: 1 }}
              />
            </div>
          </div>
          <div>
            <label style={{ ...S.label, marginBottom: 6 }}>Default Annual Work Hours</label>
            <input
              type="number"
              value={draft.annualHoursForMinWage ?? 2080}
              onChange={(e) => updateField('annualHoursForMinWage', +e.target.value)}
              step="40"
              style={{ ...S.input, fontSize: 12, padding: '6px 10px' }}
            />
          </div>
        </div>
      </div>

      {/* Employer Calculation Settings */}
      <div style={S.card}>
        <h3 style={S.heading}>Employer FICA Calculation Method</h3>
        <p style={{ fontSize: 11, color: C.textLight, margin: '0 0 12px', lineHeight: 1.5 }}>
          System-level decision on how to calculate employer FICA savings. This affects all analyses.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {ficaMethodOptions.map(opt => (
            <label key={opt.value} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', background: draft.employerFicaMethod === opt.value ? C.accentBg : C.bg, borderRadius: 8, border: `1px solid ${draft.employerFicaMethod === opt.value ? C.accent : C.borderLight}`, cursor: 'pointer' }}>
              <input
                type="radio"
                name="ficaMethod"
                value={opt.value}
                checked={draft.employerFicaMethod === opt.value}
                onChange={(e) => updateField('employerFicaMethod', e.target.value)}
                style={{ marginTop: 3 }}
              />
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: draft.employerFicaMethod === opt.value ? C.accent : C.text }}>{opt.label}</div>
                <div style={{ fontSize: 10, color: C.textLight, marginTop: 2 }}>{opt.desc}</div>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* FUTA Constants */}
      <div style={S.card}>
        <h3 style={S.heading}>FUTA (Federal Unemployment Tax) Constants</h3>
        <p style={{ fontSize: 11, color: C.textLight, margin: '0 0 12px', lineHeight: 1.5 }}>
          Policy constants. Change annually if IRS updates the FUTA rate or wage base. Users control FUTA inclusion via Analysis Controls.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ ...S.label, marginBottom: 6 }}>FUTA Rate</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <input
                type="number"
                value={((draft.futaRate ?? 0) * 100).toFixed(1)}
                onChange={(e) => updateField('futaRate', +e.target.value / 100)}
                step="0.1"
                style={{ ...S.input, fontSize: 12, padding: '6px 10px', flex: 1 }}
              />
              <span style={{ ...S.mono, fontSize: 12, color: C.accent, fontWeight: 700 }}>%</span>
            </div>
          </div>
          <div>
            <label style={{ ...S.label, marginBottom: 6 }}>FUTA Wage Base</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ ...S.mono, fontSize: 12, color: C.accent, fontWeight: 700 }}>$</span>
              <input
                type="number"
                value={draft.futaWageBase ?? 7000}
                onChange={(e) => updateField('futaWageBase', +e.target.value)}
                step="100"
                style={{ ...S.input, fontSize: 12, padding: '6px 10px', flex: 1 }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Employer Portal Defaults */}
      <div style={S.card}>
        <h3 style={S.heading}>Employer Portal Defaults</h3>
        <p style={{ fontSize: 11, color: C.textLight, margin: '0 0 12px', lineHeight: 1.5 }}>
          Default settings for the Employer Portal enrollment and onboarding flows.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 12 }}>
          <div>
            <label style={{ ...S.label, marginBottom: 6 }}>Default Onboarding Team</label>
            <p style={{ fontSize: 10, color: C.textLight, marginBottom: 6 }}>
              Team assigned to new employer onboarding requests (can be overridden per employer)
            </p>
            <input
              type="text"
              value={draft.defaultOnboardingTeam ?? 'Implementation Team'}
              onChange={(e) => updateField('defaultOnboardingTeam', e.target.value)}
              placeholder="e.g., Implementation Team, Support Team"
              style={{ ...S.input, fontSize: 12, padding: '8px 12px' }}
            />
          </div>
        </div>
      </div>

      {/* Break-Even Search Parameters */}
      <div style={S.card}>
        <h3 style={S.heading}>Break-Even Search Parameters</h3>
        <p style={{ fontSize: 11, color: C.textLight, margin: '0 0 12px', lineHeight: 1.5 }}>
          Technical parameters that control the binary search range when calculating minimum salary for eligibility.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <div>
            <label style={{ ...S.label, marginBottom: 6 }}>Minimum Salary</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ ...S.mono, fontSize: 12, color: C.accent, fontWeight: 700 }}>$</span>
              <input
                type="number"
                value={draft.breakEvenMinSalary ?? 15000}
                onChange={(e) => updateField('breakEvenMinSalary', +e.target.value)}
                step="1000"
                style={{ ...S.input, fontSize: 12, padding: '6px 10px', flex: 1 }}
              />
            </div>
          </div>
          <div>
            <label style={{ ...S.label, marginBottom: 6 }}>Maximum Salary</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ ...S.mono, fontSize: 12, color: C.accent, fontWeight: 700 }}>$</span>
              <input
                type="number"
                value={draft.breakEvenMaxSalary ?? 250000}
                onChange={(e) => updateField('breakEvenMaxSalary', +e.target.value)}
                step="1000"
                style={{ ...S.input, fontSize: 12, padding: '6px 10px', flex: 1 }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Save button at bottom if changes */}
      {hasChanges && (
        <button onClick={handleSave} style={{ ...S.btn, ...S.btnPrimary, width: 'fit-content', alignSelf: 'flex-end' }}>
          Save All Changes
        </button>
      )}
    </div>
  );
}
